import { publicSupabase } from '../supabase';

export interface SubmissionContactId {
  id: string;
  contact_id: string | null;
}

export interface PollContactIdsOptions {
  maxAttempts?: number;
  delayMs?: number;
}

export type PollContactIdsResult =
  | { status: 'ready'; data: SubmissionContactId[] }
  | { status: 'timeout'; data: SubmissionContactId[] | null }
  | { status: 'error'; data: null; error: unknown };

const DEFAULTS = {
  maxAttempts: 10,
  delayMs: 300,
} as const;

/**
 * Polling : attend que le trigger Supabase ait rempli contact_id
 * sur toutes les form_submissions demandées.
 *
 * Retourne un discriminated union :
 * - 'ready'   : toutes les submissions ont un contact_id non-null
 * - 'timeout' : tentatives épuisées, certains contact_id restent null
 * - 'error'   : la requête Supabase a échoué (réseau, RLS, etc.)
 *
 * Le caller DOIT traiter les trois cas pour éviter de signaler un succès
 * alors que le paiement n'a pas pu être initialisé.
 */
export async function pollContactIds(
  submissionIds: string[],
  options?: PollContactIdsOptions
): Promise<PollContactIdsResult> {
  const maxAttempts = options?.maxAttempts ?? DEFAULTS.maxAttempts;
  const delayMs = options?.delayMs ?? DEFAULTS.delayMs;

  let lastData: SubmissionContactId[] | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));

    // 00192 : anon n'a plus d'accès direct à form_submissions (la policy
    // `form_submissions_anon_all` exposait passeport/permis/CEAM/signature à
    // toute clé anon). La RPC SECURITY DEFINER ne rend que id + contact_id.
    const { data, error } = await publicSupabase.rpc('get_public_submission_contact_ids', {
      p_submission_ids: submissionIds,
    });

    if (error) {
      console.error('[BPI] erreur polling form_submissions', error);
      return { status: 'error', data: null, error };
    }

    lastData = (data ?? null) as SubmissionContactId[] | null;

    if (
      lastData &&
      lastData.length === submissionIds.length &&
      lastData.every((s: SubmissionContactId) => s.contact_id !== null)
    ) {
      return { status: 'ready', data: lastData };
    }
  }

  console.error(
    `[BPI] contact_id toujours null après ${maxAttempts * delayMs}ms de polling`,
    lastData
  );
  return { status: 'timeout', data: lastData };
}
